import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getPaymentLink, getStage1Price } from '../../config/paymentLinks';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createSetupIntent, savePaymentMethod, STRIPE_PUBLISHABLE_KEY } from '../../config/firebase';

// Initialize Stripe with key from firebase.js
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#aab7c4',
      },
      iconColor: '#666EE8',
    },
    invalid: {
      color: '#9e2146',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

// ============================================
// Stage 1 Card Capture Form Component
// ============================================
const CardCaptureForm = ({ project, onSuccess, onCancel, showNotification }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const price = getStage1Price(project.type, project.townhouseUnits) || 199;

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      showNotification('Error', 'Payment system not ready. Please refresh and try again.', 'error');
      return;
    }

    if (!acceptedTerms) {
      showNotification('Terms Required', 'Please accept the terms and conditions', 'warning');
      return;
    }

    if (!cardComplete) {
      showNotification('Card Required', 'Please enter valid card details', 'warning');
      return;
    }

    setProcessing(true);

    try {
      // Step 1: Create SetupIntent via Cloud Function
      console.log('Creating SetupIntent for project:', project.id);
      
      const setupIntentResult = await createSetupIntent({
        projectId: project.id || project.firebaseId,
        userEmail: project.email || project.userEmail,
        userName: project.client || project.userName,
        amount: price * 100, // Convert to cents
        projectType: project.type,
        userId: project.userId
      });

      const { clientSecret, customerId } = setupIntentResult.data;
      console.log('SetupIntent created, customerId:', customerId);

      // Step 2: Confirm SetupIntent with card details
      const cardElement = elements.getElement(CardElement);
      
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: project.client,
              email: project.email,
            },
          },
        }
      );

      if (confirmError) {
        console.error('Card confirmation error:', confirmError);
        setCardError(confirmError.message);
        showNotification('Card Error', confirmError.message, 'error');
        setProcessing(false);
        return;
      }

      console.log('SetupIntent confirmed:', setupIntent.id);

      // Step 3: Save payment method via Cloud Function
      const saveResult = await savePaymentMethod({
        setupIntentId: setupIntent.id,
        projectId: project.id || project.firebaseId
      });

      if (saveResult.data.success) {
        console.log('Payment method saved successfully');
        showNotification(
          'Card Saved Successfully!',
          'Your card has been securely saved. Work will begin immediately. You will receive your draft report within 3-5 business days.',
          'success'
        );
        onSuccess();
      } else {
        throw new Error('Failed to save payment method');
      }

    } catch (error) {
      console.error('Card capture error:', error);
      showNotification(
        'Error',
        error.message || 'Failed to save card. Please try again.',
        'error'
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Payment Information */}
      <div className="bg-gray-50 p-4 rounded">
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Project:</span>
          <span>{project.name}</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Service:</span>
          <span className="text-sm">Desktop Site Investigation & Feasibility</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">Amount:</span>
          <span className="text-xl font-bold text-green-600">${price}</span>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">💳 How Card Capture Works</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• Your card will <strong>NOT</strong> be charged now</li>
          <li>• Work begins immediately after card details are captured</li>
          <li>• Draft watermarked report delivered within 3-5 business days</li>
          <li>• Invoice sent with draft report — payment due within 5 days</li>
          <li>• Final unwatermarked report released upon payment</li>
          <li>• <strong>Auto-charge:</strong> If unpaid after 5 days, your card will be automatically charged</li>
        </ul>
      </div>

      {/* Card Element */}
      <div>
        <label className="block font-semibold mb-2">Card Details</label>
        <div className="p-3 border-2 border-gray-300 rounded-lg focus-within:border-blue-500 bg-white">
          <CardElement 
            options={cardElementOptions} 
            onChange={handleCardChange}
          />
        </div>
        {cardError && (
          <p className="text-red-600 text-sm mt-1">{cardError}</p>
        )}
      </div>

      {/* Security Badge */}
      <div className="flex items-center gap-2 text-xs bg-green-50 p-2 rounded">
        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
        </svg>
        <span>Your card details are securely processed by Stripe. We never store your full card number.</span>
      </div>

      {/* Terms */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-2">Card Capture Terms</h3>
        <div className="text-xs text-gray-600 space-y-1 mb-3">
          <p>• By providing your card details, you authorise planAIdesign to charge ${price} if payment is not made within 5 days of invoice.</p>
          <p>• Work undertaken by qualified professionals with 15+ years experience.</p>
          <p>• Professional indemnity insurance coverage.</p>
          <p>• Refunds: Services are non-refundable once work has commenced.</p>
        </div>

        <label className="flex items-start gap-2 mt-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-xs text-gray-700">
            I authorise planAIdesign to save my card details and charge ${price} if payment is not received within 5 days of invoice.
          </span>
        </label>
      </div>

      {/* Buttons */}
      <div className="flex gap-4 mt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600 disabled:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={processing || !stripe || !acceptedTerms || !cardComplete}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
        >
          {processing ? 'Saving Card...' : 'Save Card & Start Work'}
        </button>
      </div>
    </form>
  );
};

// ============================================
// Main Payment Modal Component
// ============================================
const PaymentModal = () => {
  const { 
    showPaymentModal, 
    setShowPaymentModal, 
    paymentProject,
    showNotification,
    setProjects
  } = useAppContext();

  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!showPaymentModal) {
      setAcceptedTerms(false);
      setProcessing(false);
    }
  }, [showPaymentModal]);

  if (!showPaymentModal || !paymentProject) return null;

  const isStage1 = paymentProject.stage === 1;
  const isStage2 = paymentProject.stage === 2;
  const isBundled = paymentProject.includesSiteInvestigation || paymentProject.hasCompletedStage1 === false;

  // Check if this is a Stage 1 card capture (new flow) vs invoice payment
  const isStage1CardCapture = isStage1 && 
    (paymentProject.paymentStatus === 'pending' || 
     paymentProject.status === 'awaiting-card-capture' ||
     !paymentProject.paymentMethodId);
  
  // Stage 1 invoice payment (after delivery)
  const isStage1InvoicePayment = isStage1 && 
    (paymentProject.paymentStatus === 'invoice-sent' || 
     paymentProject.status === 'invoice-sent');

  // Handle successful card capture
  const handleCardCaptureSuccess = () => {
    // Update local state
    setProjects(prev => prev.map(p => {
      if (p.id === paymentProject.id || p.firebaseId === paymentProject.firebaseId) {
        return {
          ...p,
          paymentStatus: 'card-captured',
          status: 'in-progress',
          cardCapturedAt: new Date().toISOString(),
          communications: [
            ...(p.communications || []),
            {
              type: 'system',
              sender: 'planAIdesign System',
              message: 'Card details captured successfully. Work will begin immediately.',
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            },
            {
              type: 'architect',
              sender: 'planAIdesign',
              message: 'Thank you! Your project is now in progress. You will receive your draft feasibility report within 3-5 business days.',
              timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    
    setShowPaymentModal(false);
  };

  // ============================================
  // Stage 1 Card Capture Flow (NEW)
  // ============================================
  if (isStage1CardCapture) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4">💳 Secure Card Capture</h2>
          
          <Elements stripe={stripePromise}>
            <CardCaptureForm
              project={paymentProject}
              onSuccess={handleCardCaptureSuccess}
              onCancel={() => setShowPaymentModal(false)}
              showNotification={showNotification}
            />
          </Elements>
        </div>
      </div>
    );
  }

  // ============================================
  // Stage 1 Invoice Payment (via Payment Link)
  // ============================================
  if (isStage1InvoicePayment) {
    const price = getStage1Price(paymentProject.type, paymentProject.townhouseUnits) || 199;
    
    const handleInvoicePayment = () => {
      setProcessing(true);
      
      // Save to localStorage for return handling
      const projectIdToSave = paymentProject.firebaseId || paymentProject.id;
      localStorage.setItem('pendingPaymentProjectId', String(projectIdToSave));
      localStorage.setItem('pendingPaymentStage', '1');
      localStorage.setItem('pendingPaymentType', 'invoice');
      
      // Get payment link
      const stripeUrl = getPaymentLink('1', paymentProject.type, null);
      
      if (!stripeUrl) {
        showNotification('Error', 'Payment link not configured. Please contact support.', 'error');
        setProcessing(false);
        return;
      }
      
      showNotification('Redirecting', 'Taking you to secure payment...', 'info');
      
      setTimeout(() => {
        window.location.href = stripeUrl;
      }, 1000);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4">💰 Invoice Payment</h2>
          
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">⏰ Payment Due</h4>
              <p className="text-sm text-yellow-700">
                Your draft report has been delivered. Please complete payment to receive the final unwatermarked report.
              </p>
              <p className="text-xs text-yellow-600 mt-2">
                Note: Your card will be automatically charged after 5 days if payment is not received.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded">
              <div className="flex justify-between mb-2">
                <span className="font-semibold">Project:</span>
                <span>{paymentProject.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Amount Due:</span>
                <span className="text-xl font-bold text-green-600">${price}</span>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs text-gray-700">
                I understand that paying now will release my final report immediately.
              </span>
            </label>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
              >
                Pay Later
              </button>
              <button
                onClick={handleInvoicePayment}
                disabled={processing || !acceptedTerms}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
              >
                {processing ? 'Redirecting...' : `Pay $${price} Now`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // Stage 2 Payment Flow (existing - Payment Links)
  // ============================================
  const getPaymentAmount = () => {
    if (isStage2 && paymentProject.paymentStructure) {
      const paymentsMade = paymentProject.paymentsMade || 0;
      const { paymentAmounts } = paymentProject.paymentStructure;
      if (paymentsMade < paymentAmounts.length) {
        return paymentAmounts[paymentsMade];
      }
    }
    return paymentProject.paymentAmount || 0;
  };

  const getPaymentDescription = () => {
    if (isStage2) {
      const paymentsMade = paymentProject.paymentsMade || 0;
      const totalPayments = paymentProject.paymentStructure?.totalPayments || 2;
      const baseDescription = `Payment ${paymentsMade + 1} of ${totalPayments} for Concept Design`;
      
      if (isBundled) {
        return `${baseDescription} (includes Desktop Site Investigation)`;
      }
      return baseDescription;
    }

    if (paymentProject.stage >= 3) {
      return paymentProject.paymentDescription || 'Payment for architectural services';
    }

    return 'Payment for services';
  };

  const handleStage2Payment = () => {
    if (!acceptedTerms) {
      showNotification("Terms Required", "Please accept the terms and conditions", "warning");
      return;
    }

    setProcessing(true);
    
    const paymentsMade = paymentProject.paymentsMade || 0;
    const projectIdToSave = paymentProject.firebaseId || paymentProject.id;
    
    localStorage.setItem('pendingPaymentProjectId', String(projectIdToSave));
    localStorage.setItem('pendingPaymentStage', '2');
    localStorage.setItem('pendingPaymentNumber', String(paymentsMade + 1));
    localStorage.setItem('pendingTotalPayments', String(paymentProject.paymentStructure?.totalPayments || 2));
    
    const projectDetails = {
      paymentsMade: paymentsMade,
      totalPayments: paymentProject.paymentStructure?.totalPayments || 2,
      storeys: paymentProject.storeys,
      townhouseUnits: paymentProject.townhouseUnits,
      includesSiteInvestigation: paymentProject.includesSiteInvestigation,
      hasCompletedStage1: paymentProject.hasCompletedStage1
    };
    
    const stripeUrl = getPaymentLink('2', paymentProject.type, null, projectDetails);
    
    if (!stripeUrl) {
      showNotification("Configuration Error", `Payment link not configured for this project type.`, "error");
      setProcessing(false);
      return;
    }

    showNotification("Redirecting to Stripe", "You'll be redirected to secure payment...", "info");
    
    setTimeout(() => {
      window.location.href = stripeUrl;
    }, 1000);
  };

  // Payment timeline for Stage 2
  const getPaymentTimeline = () => {
    if (!isStage2 || !paymentProject.paymentStructure) return null;
    
    const { totalPayments, paymentAmounts } = paymentProject.paymentStructure;
    const paymentsMade = paymentProject.paymentsMade || 0;
    
    return (
      <div className="bg-gray-100 p-3 rounded mt-2">
        <h4 className="text-sm font-semibold mb-2">Payment Schedule:</h4>
        <div className="space-y-1">
          {paymentAmounts.map((amount, index) => (
            <div key={index} className={`flex justify-between text-xs ${
              index < paymentsMade ? 'text-green-600' : 
              index === paymentsMade ? 'text-blue-600 font-semibold' : 
              'text-gray-500'
            }`}>
              <span>
                Payment {index + 1}: 
                {index === 0 ? ' Initial' : 
                 index === totalPayments - 1 ? ' Final' : 
                 ' Progress'}
              </span>
              <span>
                ${amount} 
                {index < paymentsMade && ' ✓'}
                {index === paymentsMade && ' ← Current'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Payment Details</h2>
        
        <div className="space-y-4">
          {/* Payment Information */}
          <div className="bg-gray-50 p-4 rounded">
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Project:</span>
              <span>{paymentProject.name}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Stage:</span>
              <span>{paymentProject.stage}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Description:</span>
              <span className="text-sm text-right">{getPaymentDescription()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Amount:</span>
              <span className="text-xl font-bold text-green-600">
                ${getPaymentAmount()}
              </span>
            </div>
          </div>

          {/* Professional Service Trust Badge */}
          <div className="flex items-center gap-2 text-xs bg-blue-50 p-2 rounded">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
            <span>Prepared by experienced professionals • Delivered via secure platform</span>
          </div>

          {/* Payment Timeline for Stage 2 */}
          {getPaymentTimeline()}

          {/* Stripe redirect message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Secure Payment via Stripe</p>
                <p>You'll be redirected to Stripe's secure payment page to complete this transaction.</p>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-2">Payment Terms</h3>
            <div className="text-xs text-gray-600 space-y-1 mb-3">
              <p>• Payment is required before work can begin.</p>
              <p>• Work undertaken by qualified professionals with 15+ years experience.</p>
              <p>• Professional indemnity insurance coverage.</p>
              <p>• Stage 2 payments are split into {paymentProject.paymentStructure?.totalPayments || 2} installments.</p>
              <p>• Refunds: Services are non-refundable once work has commenced.</p>
            </div>

            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs text-gray-700">
                I have read and accept the payment terms and conditions.
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={() => setShowPaymentModal(false)}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleStage2Payment}
            disabled={processing || !acceptedTerms}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
          >
            {processing ? 'Processing...' : `Pay $${getPaymentAmount()} via Stripe`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;